/**
 * uploadFlow — the two-phase upload pipeline shared by every customer-
 * facing surface that puts image bytes into S3 (currently
 * ImageUploadModal; future: drag-drop on the canvas).
 *
 * Mode-agnostic by design: the backend chooses where the browser PUTs
 * by setting upload_url to either:
 *
 *   direct mode: https://sfblobstore.{domain}/ignode-annotations/{key}?signature=…
 *   proxy mode:  https://annotate.{domain}/api/v1/org/{orgId}/upload-proxy/{token}
 *
 * The client follows whatever URL it got — no `if (mode === ...)` here.
 *
 * 3 calls per file:
 *
 *   1. POST /projects/:id/images/presign     →  PresignResponse
 *   2. PUT  <upload_url>  (the file bytes)   →  204 on success
 *   3. POST /images/:id/finalize             →  ImageMeta (state='ready')
 *
 * If step 2 fails, step 3 is skipped — the orphan-cleanup goroutine
 * reaps the pending row after pending_expires_at. The client can
 * surface the error to the user.
 *
 * Idempotency: re-calling finalize after success is a no-op (server
 * detects width/height already populated and returns the existing row).
 */
import { imagesApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import { makeThumbnailBlob } from './makeThumbnail';
import type { ImageMeta } from '../types/api';

export class UploadFlowError extends Error {
    /** Stage that failed: 'presign' | 'put' | 'finalize'. */
    stage: 'presign' | 'put' | 'finalize';
    /** When the original error was an APIClientError, its structured code (e.g. 'annotation_quota_exceeded'). */
    apiCode?: string;
    /** When the original error was an APIClientError, the structured field errors. */
    fields?: Array<{ field: string; issue: string }>;
    /** D.2.3 — quota errors include scope/current/limit so the UI can render
     *  a specific quota-exceeded modal. Populated only when the upstream
     *  APIClientError body carried these fields. */
    scope?: string;
    current?: number;
    limit?: number;
    constructor(
        stage: 'presign' | 'put' | 'finalize',
        message: string,
        opts: {
            apiCode?: string;
            fields?: Array<{ field: string; issue: string }>;
            scope?: string;
            current?: number;
            limit?: number;
        } = {},
    ) {
        super(message);
        this.stage = stage;
        this.apiCode = opts.apiCode;
        this.fields = opts.fields;
        this.scope = opts.scope;
        this.current = opts.current;
        this.limit = opts.limit;
    }
}

/**
 * Upload a single image. Returns the finalized ImageMeta (state='ready').
 *
 * Caller is responsible for any client-side normalization (EXIF strip,
 * JPEG re-encode for unsupported subsampling) BEFORE handing the file
 * in. See utils/normalizeImage.ts.
 */
export async function uploadImageFile(
    projectId: string,
    file: File,
    orgId?: string,
): Promise<ImageMeta> {
    // ---- Step 1: presign ----
    let presign;
    try {
        presign = await imagesApi.presign(
            projectId,
            {
                filename: file.name,
                size: file.size,
                content_type: file.type || 'application/octet-stream',
            },
            orgId,
        );
    } catch (err) {
        if (err instanceof APIClientError) {
            throw new UploadFlowError('presign', err.body.message || err.code, {
                apiCode: err.code,
                fields: err.body.fields,
                scope: err.body.scope,
                current: err.body.current,
                limit: err.body.limit,
            });
        }
        throw new UploadFlowError('presign', err instanceof Error ? err.message : String(err));
    }

    // ---- Step 2: PUT bytes to upload_url + thumbnail in parallel ----
    // Original PUT must succeed; thumbnail PUT is best-effort. If thumb
    // fails, backend's lazy-thumbnail path picks it up on first
    // /thumbnail GET.
    //
    // The presign URL already encodes everything the server needs to
    // authorise the upload:
    //   - direct mode: AWS v4 query signature binds to method/host/key/expiry
    //   - proxy mode : HMAC token in the path binds to image_id/s3_key/expiry
    // We do NOT set credentials (cookies) — direct mode is a different
    // origin (sfblobstore.*) and the SeaweedFS CORS rule responds without
    // Access-Control-Allow-Credentials, so the browser would block it.
    // Proxy mode runs same-origin so the cookie tags along automatically
    // for the IGNODE proxy's permission check.
    const putOriginal = async (): Promise<Response> => {
        // Honor the method + headers the backend returned. Direct mode
        // signs the URL with a specific Content-Type so the PUT MUST
        // send the same value — anything else fails the AWS v4 sig.
        const putHeaders: Record<string, string> = presign.headers && Object.keys(presign.headers).length > 0
            ? presign.headers
            : { 'Content-Type': file.type || 'application/octet-stream' };
        return fetch(presign.upload_url, {
            method: presign.method || 'PUT',
            headers: putHeaders,
            body: file,
        });
    };

    // Thumbnail PUT — only if the backend handed us a URL. Older
    // backends don't send `thumbnail_upload_url`; in that case we skip
    // the client-side thumb and the backend's lazy path will fill in.
    const putThumbnail = async (): Promise<void> => {
        if (!presign.thumbnail_upload_url) return;
        const thumbBlob = await makeThumbnailBlob(file);
        if (!thumbBlob) return; // decode failed; lazy fallback covers it
        const res = await fetch(presign.thumbnail_upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/jpeg' },
            body: thumbBlob,
        });
        if (!res.ok) {
            // Don't throw — just log and let lazy fallback handle it.
            // eslint-disable-next-line no-console
            console.warn('thumbnail PUT failed', res.status, 'lazy fallback will cover');
        }
    };

    // Run both PUTs in parallel. Original failure aborts; thumb failure
    // is swallowed by the wrapper above.
    let putRes: Response;
    try {
        const [origRes] = await Promise.all([
            putOriginal(),
            putThumbnail().catch((e) => {
                // eslint-disable-next-line no-console
                console.warn('thumbnail step error (non-fatal):', e);
            }),
        ]);
        putRes = origRes;
    } catch (err) {
        throw new UploadFlowError('put', err instanceof Error ? err.message : String(err));
    }
    if (!putRes.ok) {
        // S3 returns XML on error; SeaweedFS returns a short string. We
        // don't surface either to the user verbatim — the customer can't
        // act on it. Just stage + status.
        throw new UploadFlowError(
            'put',
            `upload failed (HTTP ${putRes.status})`,
            { apiCode: 'upload_put_failed' },
        );
    }

    // ---- Step 3: finalize ----
    try {
        return await imagesApi.finalize(presign.image_id, orgId);
    } catch (err) {
        if (err instanceof APIClientError) {
            throw new UploadFlowError('finalize', err.body.message || err.code, {
                apiCode: err.code,
                fields: err.body.fields,
            });
        }
        throw new UploadFlowError('finalize', err instanceof Error ? err.message : String(err));
    }
}
